import {
  SELECTIVE_PRIMARY_NAV_IDS,
  resolveSelectiveNavigationRoute,
} from '@/lib/selectiveNavigationContract.js';

export const CONTEXTUAL_PANEL_MODES = Object.freeze({
  NORMAL: 'normal',
  REVIEW_COMPOSER: 'review_composer',
  REPLY_COMPOSER: 'reply_composer',
  AUTH_INTENT: 'auth_intent',
});

export const CONTEXTUAL_PANEL_TARGET_TYPES = Object.freeze({
  DEFAULT: 'default',
  GAME: 'game',
  ARTICLE: 'article',
  DASHBOARD: 'dashboard',
});

export const GAME_DETAILS_PANEL_STATES = Object.freeze({
  GUEST: 'guest',
  LOGGED_IN_NO_PROVIDER: 'logged_in_no_provider',
  LOGGED_IN_STEAM_CONNECTED_GAME_FOUND: 'logged_in_steam_connected_game_found',
  LOGGED_IN_STEAM_CONNECTED_GAME_NOT_FOUND: 'logged_in_steam_connected_game_not_found',
  LOGGED_IN_MANUAL_WISHLIST: 'logged_in_manual_wishlist',
  LOGGED_IN_MANUAL_PLAYED_WITHOUT_OWNING: 'logged_in_manual_played_without_owning',
  LOGGED_IN_MANUAL_OWNED: 'logged_in_manual_owned',
});

export const MANUAL_GAME_RELATIONSHIP_STATES = Object.freeze({
  WISHLIST: 'wishlist',
  PLAYED_WITHOUT_OWNING: 'played_without_owning',
  OWNED_MANUAL: 'owned_manual',
});

const DEFAULT_GAME_OWNERSHIP_SECTION_ID = 'game-ownership';

const normalizePathname = (pathname = '') => pathname.toLowerCase();

export function resolveContextualPanelRoute(pathname = '') {
  const path = normalizePathname(pathname);

  if (path === '/games' || path.startsWith('/games/') || path.startsWith('/gamedetails')) {
    return CONTEXTUAL_PANEL_TARGET_TYPES.GAME;
  }

  if (path === '/news' || path.startsWith('/news/') || path.startsWith('/articledetails')) {
    return CONTEXTUAL_PANEL_TARGET_TYPES.ARTICLE;
  }

  if (path.startsWith('/dashboard')) {
    return CONTEXTUAL_PANEL_TARGET_TYPES.DASHBOARD;
  }

  return CONTEXTUAL_PANEL_TARGET_TYPES.DEFAULT;
}

export function shouldEnableContextualDrawer({
  pathname,
  isSelectivePublic = false,
} = {}) {
  if (!isSelectivePublic) {
    return true;
  }

  const { activePrimaryId } = resolveSelectiveNavigationRoute(pathname);
  return (
    activePrimaryId === SELECTIVE_PRIMARY_NAV_IDS.GAMES ||
    activePrimaryId === SELECTIVE_PRIMARY_NAV_IDS.NEWS
  );
}

export function buildGameOwnershipSection({ gamePanelData, labels }) {
  const fallback = {
    id: DEFAULT_GAME_OWNERSHIP_SECTION_ID,
    title: labels.gameRelationshipTitle,
    content: labels.gameRelationshipContent,
    badge: labels.comingSoonBadge,
  };

  if (!gamePanelData?.section) {
    return fallback;
  }

  return {
    id: gamePanelData.section.id || DEFAULT_GAME_OWNERSHIP_SECTION_ID,
    ...gamePanelData.section,
    title: gamePanelData.section.title || fallback.title,
    content: gamePanelData.section.content || fallback.content,
    badge: gamePanelData.section.badge || fallback.badge,
  };
}

const resolveArticlePanelMode = ({ authIntent, replyComposer }) => {
  if (authIntent) {
    return CONTEXTUAL_PANEL_MODES.AUTH_INTENT;
  }

  if (replyComposer) {
    return CONTEXTUAL_PANEL_MODES.REPLY_COMPOSER;
  }

  return CONTEXTUAL_PANEL_MODES.NORMAL;
};

export function buildContextualPanelModel({
  pathname,
  user,
  gamePanelData,
  labels,
  dashboardGuestSections = [],
  isSelectivePublic = false,
}) {
  const normalizedPathname = normalizePathname(pathname);
  const targetType = resolveContextualPanelRoute(pathname);
  const authIntent = !user ? gamePanelData?.authIntent || null : null;

  if (targetType === CONTEXTUAL_PANEL_TARGET_TYPES.GAME) {
    if (normalizedPathname === '/games') {
      return {
        targetType,
        mode: CONTEXTUAL_PANEL_MODES.NORMAL,
        title: labels.gamesTitle,
        description: labels.gamesDescription,
        sections: [],
        highlightAvatar: false,
        reviewComposer: null,
        authIntent: null,
      };
    }

    const discussionSection = {
      id: 'game-discussions',
      title: labels.gameDiscussionTitle,
      content: labels.gameDiscussionContent,
    };

    return {
      targetType,
      mode: authIntent ? CONTEXTUAL_PANEL_MODES.AUTH_INTENT : CONTEXTUAL_PANEL_MODES.NORMAL,
      title: labels.gameTitle,
      description: isSelectivePublic
        ? labels.selectiveGameDescription
        : labels.gameDescription,
      sections: isSelectivePublic
        ? [discussionSection]
        : [buildGameOwnershipSection({ gamePanelData, labels }), discussionSection],
      highlightAvatar: Boolean(gamePanelData?.highlightAvatar),
      reviewComposer: gamePanelData?.reviewComposer || null,
      authIntent,
    };
  }

  if (targetType === CONTEXTUAL_PANEL_TARGET_TYPES.ARTICLE) {
    if (normalizedPathname === '/news' || normalizedPathname === '/news/archive') {
      return {
        targetType,
        mode: CONTEXTUAL_PANEL_MODES.NORMAL,
        title: labels.newsTitle,
        description: labels.newsDescription,
        sections: [],
        highlightAvatar: false,
        replyComposer: null,
        authIntent: null,
      };
    }

    const dynamicArticleSection = gamePanelData?.articleSection || null;

    return {
      targetType,
      mode: resolveArticlePanelMode({
        authIntent,
        replyComposer: gamePanelData?.replyComposer,
      }),
      title: labels.articleTitle,
      description: isSelectivePublic
        ? labels.selectiveArticleDescription
        : labels.articleDescription,
      sections: dynamicArticleSection
        ? [dynamicArticleSection]
        : [
            {
              id: 'article-digest',
              title: labels.articleDiscussionTitle,
              content: labels.articleDiscussionContent,
              badge: labels.comingSoonBadge,
            },
      ],
      highlightAvatar: Boolean(gamePanelData?.highlightAvatar),
      replyComposer: gamePanelData?.replyComposer || null,
      authIntent,
    };
  }

  if (targetType === CONTEXTUAL_PANEL_TARGET_TYPES.DASHBOARD) {
    return {
      targetType,
      mode: CONTEXTUAL_PANEL_MODES.NORMAL,
      title: labels.dashboardTitle,
      description: user ? labels.dashboardDescriptionAuthenticated : labels.dashboardDescriptionGuest,
      sections: user || isSelectivePublic ? [] : dashboardGuestSections,
    };
  }

  return {
    targetType,
    mode: CONTEXTUAL_PANEL_MODES.NORMAL,
    title: labels.defaultTitle,
    description: labels.defaultDescription,
    sections: [],
  };
}
